import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import CardContent from '@mui/material/CardContent'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { Link as RouterLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import type { UnitSummary } from '../../types/api'
import { useFormatters } from '../../hooks/useFormatters'

/**
 * A cottage card.
 *
 * The feature chips lead with an emoji rather than a Material icon. On a listing like this
 * the reader is scanning for one specific thing (is there a sauna, can I bring the dog) and
 * a colourful glyph is found faster than a monochrome pictogram. Functional controls
 * elsewhere in the app still use Material icons, where consistency matters more than speed
 * of recognition.
 */
export function UnitCard({ unit }: { unit: UnitSummary }) {
  const { t } = useTranslation()
  const { money } = useFormatters()

  const features = [
    unit.hasSauna && { icon: '🧖', label: t('houses.featureSauna') },
    unit.hasBoat && { icon: '🚣', label: t('houses.featureBoat') },
    unit.hasFireplace && { icon: '🔥', label: t('houses.featureFireplace') },
    unit.petsAllowed && { icon: '🐕', label: t('houses.featurePets') },
  ].filter(Boolean) as { icon: string; label: string }[]

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': { boxShadow: 6, transform: 'translateY(-3px)' },
        '&:focus-within': { boxShadow: 6 },
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={`/houses/${unit.slug}`}
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        <Box
          sx={{
            position: 'relative',
            aspectRatio: '4 / 3',
            bgcolor: 'primary.light',
            backgroundImage: unit.coverImageUrl ? `url(${unit.coverImageUrl})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {!unit.coverImageUrl && (
            <Box aria-hidden sx={{ fontSize: '3rem', opacity: 0.5 }}>
              🏡
            </Box>
          )}
          <Chip
            size="small"
            label={unit.unitType === 'MAIN_HOUSE' ? t('houses.mainHouse') : t('houses.lakeHouse')}
            sx={{
              position: 'absolute',
              top: 12,
              insetInlineStart: 12,
              bgcolor: 'rgba(255,255,255,0.94)',
              fontWeight: 600,
            }}
          />
        </Box>

        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.25, p: 2.5 }}>
          <Typography variant="h4" component="h3">
            {unit.name}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {unit.summary}
          </Typography>

          <Stack direction="row" spacing={1} sx={{ color: 'text.secondary', flexWrap: 'wrap', rowGap: 0.5 }}>
            <Typography variant="body2">👥 {t('houses.sleeps', { count: unit.maxGuests })}</Typography>
            <Typography variant="body2">·</Typography>
            <Typography variant="body2">
              🛏️ {t('houses.bedrooms', { count: unit.bedrooms })}
            </Typography>
            {unit.sizeSqm && (
              <>
                <Typography variant="body2">·</Typography>
                <Typography variant="body2">📐 {t('houses.size', { count: unit.sizeSqm })}</Typography>
              </>
            )}
          </Stack>

          <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
            {features.map((feature) => (
              <Chip
                key={feature.label}
                size="small"
                variant="outlined"
                label={
                  <Box component="span" aria-label={feature.label}>
                    {feature.icon} {feature.label}
                  </Box>
                }
              />
            ))}
          </Stack>

          <Box sx={{ pt: 1, display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
            <Typography variant="caption" color="text.secondary">
              {t('common.from')}
            </Typography>
            <Typography variant="h5" color="primary.main">
              {money(unit.basePricePerNight)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('common.perNight')}
            </Typography>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  )
}
